/*!
 * scroll-troll.js
 *
 * Prevent touch scrolling on the `document.body` element
 *
 * This is unfortunately necessary to prevent viewport issues on iOS
 * due to the Universal Links App Banner which appears above the top
 * of the page and cannot be dismissed, hidden, or targeted with CSS.
 *
 *      _,/'/;   __________________
 *    /__,/ |   |                  |
 *     oo \,/   |  Thanks, Apple.  |
 *    (/__/    <___________________|
 *   ,/.-. `.
 *  /(( . )) \
 *  `-'---'`-'
 *   `--'--'
 *
 * Motivation: https://medium.com/mobile-growth/5beb15f88a29
 *             (Specifically, "The Phantom Banner" section)
 * Prior art: https://stackoverflow.com/a/26853900
 */

module.exports = {
  preventTouchBodyScrolling: function () {
    window.addEventListener('touchstart', handleTouchStart)
  },
  allowTouchBodyScrolling: function () {
    window.removeEventListener('touchstart', handleTouchStart)
  }
}

function handleTouchStart(startEvent) {
  const { target: touchTarget } = startEvent.targetTouches[0]
  const scrollDirections = calculateTouchScrollDirections(touchTarget)
  let isTouchScrollingBody = false

  function handleTouchMove(moveEvent) {
    // If we're not scrolling inner content, we're scrolling document.body
    isTouchScrollingBody = isTouchScrollingBody ||
      !isTouchScrollingInnerContent(startEvent, moveEvent, scrollDirections)

    // Stop the body scroll so that the Universal Link banner doesn't scroll in
    if (isTouchScrollingBody) {
      moveEvent.preventDefault()
    }
  }

  function handleTouchEnd() {
    window.removeEventListener('touchmove', handleTouchMove)
    window.removeEventListener('touchend', handleTouchEnd)
  }

  window.addEventListener('touchmove', handleTouchMove)
  window.addEventListener('touchend', handleTouchEnd)
}

function isTouchScrollingInnerContent(startEvent, moveEvent, scrollDirections) {
  const { screenX: startScreenX, screenY: startScreenY } = startEvent.targetTouches[0]
  const { screenX: moveScreenX, screenY: moveScreenY } = moveEvent.targetTouches[0]
  return (
    ( scrollDirections.up    && moveScreenY > startScreenY ) ||
    ( scrollDirections.down  && moveScreenY < startScreenY ) ||
    ( scrollDirections.left  && moveScreenX > startScreenX ) ||
    ( scrollDirections.right && moveScreenX < startScreenX )
  )
}

function calculateTouchScrollDirections(touchTarget) {
  // A Boolean map indicating which directions the touched element
  // (or any scrollable parents up to but excluding document.body) can be scrolled
  return {
    up: recursivelyTest(touchTarget, ({ scrollTop }) => {
      return scrollTop > 0
    }),
    down: recursivelyTest(touchTarget, ({ scrollTop, scrollHeight, clientHeight }) => {
      return scrollHeight > clientHeight && scrollHeight - clientHeight > scrollTop
    }),
    left: recursivelyTest(touchTarget, ({ scrollLeft }) => {
      return scrollLeft > 0
    }),
    right: recursivelyTest(touchTarget, ({ scrollLeft, scrollWidth, clientWidth }) => {
      return scrollWidth > clientWidth && scrollWidth - clientWidth > scrollLeft
    })
  }
}

function recursivelyTest(element, testFunction) {
  if (!element || element === document.body) {
    return false
  }
  if (testFunction(element)) {
    return true
  }
  return recursivelyTest(element.parentNode, testFunction)
}
